/**
 * App-shell chrome shared by the Pools and Splash tabs.
 *
 * Two concerns live here, both of which have to look and behave identically on
 * every list-style view (and in both cities):
 *
 *  1. **Skeleton cards** (18c5d959). The views used to `await loadPools()`
 *     BEFORE writing any HTML, so a slow catalog request left the visitor
 *     staring at a blank blue page — no header, no tabs, no spinner. The views
 *     now paint their chrome synchronously with `skeletonListHtml()` in the
 *     `.pool-list`, and swap in real cards when the catalog resolves.
 *  2. **The name search box** (d8b45a64). One markup helper + one wiring helper
 *     so the Pools and Splash tabs can never drift apart.
 *
 * Everything here is a pure string helper (or DOM wiring over stable
 * `data-role` / `data-action` hooks), keeping the views re-render-safe.
 */

/**
 * Comment markers wrapping the skeleton block. They cost nothing in the
 * shipped HTML and give tests (and anyone reading a DOM dump) an unambiguous
 * handle on "this is the pre-data placeholder", independent of the class names.
 */
export const SKELETON_START = "<!--skeleton-->";
export const SKELETON_END = "<!--/skeleton-->";

/**
 * Placeholder cards shown in `.pool-list` until the catalog lands. Sized to
 * roughly match a real card so the swap doesn't shove the page around, marked
 * `aria-hidden` (they carry no information), and preceded by a visually hidden
 * live region so a screen reader hears "Loading…" instead of silence.
 *
 * The shimmer is pure CSS and collapses to a static block under
 * `prefers-reduced-motion` (see `.sk` in style.css).
 */
export function skeletonListHtml(count = 5): string {
  const card = `
    <article class="pool-card skeleton-card" aria-hidden="true">
      <div class="sk sk-title"></div>
      <div class="sk sk-addr"></div>
      <div class="sk sk-tile"></div>
    </article>`;
  return `${SKELETON_START}
    <p class="sr-only" role="status">Loading…</p>${Array.from({ length: count }, () => card).join("")}
  ${SKELETON_END}`;
}

/**
 * The filter-as-you-type search box that sits above the list on both tabs.
 * `label` is the accessible name (the visible placeholder stays short), e.g.
 * "Search pools by name" / "Search splash pads by name".
 *
 * `type="search"` gets the right mobile keyboard; the browser's own cancel
 * button is suppressed in CSS in favour of our own 44px-tall clear control.
 */
export function searchBoxHtml(label: string): string {
  return `
    <div class="search-row">
      <div class="search-box">
        <input
          class="search-input"
          type="search"
          data-role="search-input"
          inputmode="search"
          enterkeyhint="search"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          placeholder="Search by name"
          aria-label="${label}" />
        <button class="search-clear" type="button" data-action="search-clear" aria-label="Clear search" hidden>&times;</button>
      </div>
    </div>`;
}

/** Handle returned by `wireSearchBox` so a view can reset the box from elsewhere. */
export interface SearchBox {
  /** Empty the input and notify the view (used by the empty-state clear control). */
  clear(): void;
}

/**
 * Wire the search box painted by `searchBoxHtml`.
 *
 * `onChange` fires debounced (~150ms) with the raw query so the view can
 * re-render the in-memory list — deliberately NOT a URL round-trip, which
 * would re-run the whole view (and the router's scroll-to-top) on every
 * keystroke. The clear (×) button shows/hides immediately, undebounced.
 *
 * Returns a no-op handle when the box isn't in the DOM (e.g. the string-only
 * test mocks), so callers never have to null-check.
 */
export function wireSearchBox(
  app: HTMLElement,
  onChange: (query: string) => void,
  delayMs = 150,
): SearchBox {
  const input = app.querySelector<HTMLInputElement>('[data-role="search-input"]');
  if (!input) return { clear: () => {} };
  const clearBtn = app.querySelector<HTMLButtonElement>('[data-action="search-clear"]');

  let timer: ReturnType<typeof setTimeout> | undefined;
  const showClear = (q: string): void => {
    if (clearBtn) clearBtn.hidden = q.length === 0;
  };
  const emit = (q: string, debounce: boolean): void => {
    if (timer !== undefined) clearTimeout(timer);
    if (!debounce) {
      onChange(q);
      return;
    }
    timer = setTimeout(() => onChange(q), delayMs);
  };

  input.addEventListener("input", () => {
    showClear(input.value);
    emit(input.value, true);
  });
  // Enter shouldn't wait out the debounce (and shouldn't submit anything).
  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") {
      e.preventDefault();
      emit(input.value, false);
    }
  });

  const clear = (): void => {
    input.value = "";
    showClear("");
    emit("", false);
  };
  clearBtn?.addEventListener("click", () => {
    clear();
    input.focus();
  });

  return { clear };
}
